import { Flex, Icon, IconProps, Button } from '@cad-arc';
import { CautionLevelAttrId } from '../../socket/stream-types';
import styles from './location-caution-marker.module.css';
import { cautionLevelColors } from '../helpers/styles';
import { colors } from '../../colors/colors';

type TLocationCautionMarkerAdditionalProps =
    | {
          /**
           * `nearbyPriority1` cautions will only display an outline
           * `currentLocation` cautions will display a filled marker with no outline
           */
          type?: 'currentLocation';
          cautionLevelAttrId: CautionLevelAttrId;
      }
    | { type?: 'nearbyPriority1' };

type TAllowedIconProps = Pick<IconProps, 'size' | 'className'>;
type TAllowedHandlers = {
    onMouseDown?: React.MouseEventHandler;
    onClick?: React.MouseEventHandler;
    onFocus?: React.FocusEventHandler;
    onKeyDown?: React.KeyboardEventHandler;
};

type TLocationCautionMarkerProps = TLocationCautionMarkerAdditionalProps &
    TAllowedIconProps &
    TAllowedHandlers;

const getColorProps = (params: TLocationCautionMarkerAdditionalProps) => {
    if (params.type === 'nearbyPriority1') {
        return {
            color: colors.cautionMarkerNearby,
        };
    } else if (params.type === 'currentLocation') {
        return {
            color: cautionLevelColors[params.cautionLevelAttrId],
        };
    } else {
        return {};
    }
};

const LocationCautionMarker = ({
    onMouseDown,
    onClick,
    onFocus,
    onKeyDown,
    ...props
}: TLocationCautionMarkerProps) => {
    const colorProps = getColorProps(props);
    const isNearByCaution = props.type === 'nearbyPriority1';
    const icon = (
        <Icon icon={isNearByCaution ? 'AlertOutline' : 'Alert'} {...props} {...colorProps} />
    );
    return onClick || onMouseDown || onFocus || onKeyDown ? (
        <Flex>
            <Button
                className={styles['location-caution-marker']}
                variant="ghost"
                onClick={onClick}
                onMouseDown={onMouseDown}
                onFocus={onFocus}
                onKeyDown={onKeyDown}
            >
                {icon}
            </Button>
        </Flex>
    ) : (
        icon
    );
};

/**
 * If a location has both a caution of its own, as well as nearby cautions,
 * always prioritize showing /its own/ caution over the nearby caution
 */
export const PrioritizedLocationCautionMarker = (
    props: {
        highestPriorityLocationCaution?: number;
        isNearbyLocationCautionPriority1?: boolean;
    } & TAllowedIconProps &
        TAllowedHandlers
) => {
    if (props.highestPriorityLocationCaution) {
        return (
            <LocationCautionMarker
                type="currentLocation"
                cautionLevelAttrId={props.highestPriorityLocationCaution as CautionLevelAttrId}
                {...props}
            />
        );
    } else if (props.isNearbyLocationCautionPriority1) {
        return <LocationCautionMarker type="nearbyPriority1" {...props} />;
    } else {
        // We shouldn't get here, but if we do, don't render a marker
        return <></>;
    }
};
